import { createContext, useState, useCallback } from "react";
import axiosInstance from "../services/axiosConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BaseEnum from "../enums/BaseEnum";
import { formatStatusText } from "utils/statusFormatter";

export const OrdersContext = createContext();

// eslint-disable-next-line react/prop-types
export const OrdersProvider = ({ children }) => {
  const [ordersData, setOrdersData] = useState({ rows: [], columns: [] });
  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("tr-TR");
  };

  const handleUnauthorized = useCallback(() => {
    toast.warn(`Bu içeriği görüntüleme yetkiniz yok.`, { position: "top-right" });
  }, []);

  const roleEndpoints = {
    ROLE_SALES_RESPONSIBLE: ["sales/my-orders"],
    ROLE_LOGISTICS_OFFICER: ["logistics/orders"],
    ROLE_ACCOUNTANT: ["accountant/invoice/pending-orders"],
    ROLE_MANAGER: ["manager/orders"],
    ROLE_RESOURCE: ["resource/orders"],
    ROLE_CUSTOMER: ["sales/customer/orders"],
  };
  const defaultEndpoints = ["manager/orders"];
  const orderDetailEndpoints = {
    ROLE_RESOURCE: "resource/order",
    ROLE_LOGISTICS_OFFICER: "logistics/order",
    ROLE_ACCOUNTANT: "accountant/order",
    ROLE_MANAGER: "manager/order",
  };

  const fetchOrdersData = useCallback(
    async (role) => {
      const endpoints = roleEndpoints[role] || defaultEndpoints;

      try {
        const allData = await Promise.all(
          endpoints.map(async (endpoint) => {
            const response = await axiosInstance.get(endpoint);
            return response.data;
          })
        );

        const isCustomerRole = role === "ROLE_CUSTOMER";
        const isAccountantRole = role === "ROLE_ACCOUNTANT";

        const rows = allData.flat().map((item) => {
          const requestForm = item.requestFormDTO || item.requestForm;
          const customer = requestForm?.customer || item.customer;
          const orderDateValue =
            requestForm?.requestedDeliveryDate || item.deliveryDate || item.orderDate;

          return {
            id: item.id ?? item.orderId ?? item.requestId,
            customerName: customer?.name || "N/A",
            orderDate: formatDate(orderDateValue),
            status: isCustomerRole ? item.customerStage || item.status : item.status,
            totalAmount: requestForm?.price ?? item.price ?? item.amount,
            paymentType: requestForm?.paymentType ?? item.paymentType,
            term: requestForm?.term ?? item.term,
            pricePerTon: requestForm?.pricePerTon ?? item.pricePerTon,
            logisticsStatus: isCustomerRole
              ? undefined
              : formatStatusText(item.logisticsStatus || item.logistics?.status),
            salesInvoiceStatus: isCustomerRole ? undefined : item.salesInvoiceStatus,
            logisticsInvoiceStatus: isCustomerRole ? undefined : item.logisticsInvoiceStatus,
            customerStage: item.customerStage,
            fullData: item,
          };
        });

        const columns = isAccountantRole
          ? [
              { field: "id", headerName: "ID", width: 90 },
              { field: "customerName", headerName: BaseEnum.NAME, width: 150 },
              { field: "orderDate", headerName: BaseEnum.REQUESTED_DELIVERY_DATE, width: 150 },
              { field: "status", headerName: BaseEnum.STATUS, width: 150 },
              { field: "totalAmount", headerName: BaseEnum.PRICE, width: 150 },
              { field: "paymentType", headerName: BaseEnum.PAYMENT_TYPE, width: 150 },
              { field: "term", headerName: BaseEnum.TERM, width: 120 },
              { field: "pricePerTon", headerName: BaseEnum.PRICE_PER_TON, width: 150 },
            ]
          : [
              { field: "id", headerName: "ID", width: 90 },
              { field: "customerName", headerName: BaseEnum.NAME, width: 150 },
              { field: "orderDate", headerName: BaseEnum.REQUESTED_DELIVERY_DATE, width: 150 },
              { field: "status", headerName: BaseEnum.STATUS, width: 150 },
              { field: "totalAmount", headerName: BaseEnum.PRICE, width: 150 },
              ...(!isCustomerRole
                ? [{ field: "logisticsStatus", headerName: BaseEnum.LOGISTICS_STATUS, width: 150 }]
                : []),
              ...(!isCustomerRole
                ? [
                    {
                      field: "salesInvoiceStatus",
                      headerName: BaseEnum.SALES_INVOICE_STATUS,
                      width: 150,
                    },
                  ]
                : []),
              ...(!isCustomerRole
                ? [
                    {
                      field: "logisticsInvoiceStatus",
                      headerName: BaseEnum.LOGISTICS_INVOICE_STATUS,
                      width: 150,
                    },
                  ]
                : []),
            ];

        setOrdersData({ rows, columns });
        return { rows, columns };
      } catch (error) {
        if (error.response?.status === 403) handleUnauthorized();
        console.error(`Failed to fetch data for role ${role}:`, error);
        return { rows: [], columns: [] };
      }
    },
    [handleUnauthorized]
  );

  const getOrderById = useCallback(
    async (id, role) => {
      try {
        const baseEndpoint = orderDetailEndpoints[role] || "sales/get-order";
        const response = await axiosInstance.get(`${baseEndpoint}/${id}`);
        return response.data;
      } catch (error) {
        if (error.response?.status === 403) handleUnauthorized();
        console.error(`Failed to fetch order with ID ${id}:`, error);
        return null;
      }
    },
    [handleUnauthorized]
  );

  return (
    <OrdersContext.Provider value={{ ...ordersData, fetchOrdersData, getOrderById }}>
      {children}
    </OrdersContext.Provider>
  );
};
